import {
  EditIcon,
  InfoFilledIcon,
  OccupantsFilledIcon,
  PhoneFilledIcon,
  TimeFilledIcon,
  Tooltip,
} from '@calizahq/react-components-v2'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'

import { BottomSheet, GuardedLink, History, Tag } from '@/components'
import { RecordType } from '@/graphql/queries/fieldHistoryLogs/fieldHistoryLogs.queries'
import { useToggle } from '@/hooks'
import { ScheduledCommunicationList, ScheduledCommunicationStatus } from '@/types/ScheduledCommunication'

import { EditScheduledCommunicationForm } from './EditScheduledCommunication/EditScheduledCommunicationForm'

export const ScheduledCommunicationListColumns = (refetchOnSuccess: () => void) => {
  return [
    {
      Header: 'Scheduled Datetime',
      accessor: 'scheduledDatetime',
      width: 40,
      Cell: ({ row }: { row: { original: ScheduledCommunicationList } }) => {
        const { scheduledDatetime, reservation } = row.original
        const timezone = reservation.home.property.market.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

        try {
          const time = formatInTimeZone(scheduledDatetime, timezone, 'MMM d yyyy, HH:mm a z')
          return time
        } catch (e) {
          return ''
        }
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: 30,
    },
    {
      Header: 'Destination',
      accessor: 'destination',
      width: 40,
      Cell: ({ row }: { row: { original: ScheduledCommunicationList } }) => {
        const { destination, reservation } = row.original
        const { fullName } = reservation.member

        return (
          <div className="flex flex-wrap underline mr-4 ml-2">
            {destination ? (
              <a href={`dialpad://${destination}`} rel="noreferrer" target="_blank">
                <div className="flex flex-row">
                  <div>
                    <Tooltip text="Call with Dialpad">
                      <PhoneFilledIcon className="w-6 mr-2" height={28} width={28} />
                    </Tooltip>
                  </div>
                  <div className="mt-1">
                    {fullName}: {destination}
                  </div>
                </div>
              </a>
            ) : null}
          </div>
        )
      },
    },
    {
      Header: 'Content',
      accessor: 'content',
      width: 75,
    },
    {
      Header: 'Tags',
      width: 70,
      Cell: ({ row }: { row: { original: ScheduledCommunicationList } }) => {
        const { lexit, flightxInProgress, professionalHousing, standby, fromBarsala } = row.original.reservation
        const isLexit = !!lexit
        const isFlightX = !!flightxInProgress
        const isStandby = !!standby
        const isCorporateHousing = !!professionalHousing
        const isFromBarsala = !!fromBarsala

        return (
          <div className="flex flex-wrap w-full items-center justify-center">
            {isCorporateHousing && <Tag className="mr-1 text-sm text-tag-green-dark tag-green-light">CoHo</Tag>}
            {isStandby && <Tag className="mr-1 text-sm text-tag-grey-dark tag-grey-light">Standby</Tag>}
            {isFromBarsala && <Tag className="mr-1 text-sm text-tag-blue-dark tag-blue-light">From Barsala</Tag>}
            {isFlightX && (
              <Tag className="mr-1 text-sm" variant={`error`}>
                Flight X
              </Tag>
            )}
            {isLexit && (
              <Tag className="mr-1 text-sm" variant={`error`}>
                Lexit
              </Tag>
            )}
          </div>
        )
      },
    },
    {
      accessor: 'id',
      width: 25,
      canResize: false,
      Cell: ({ row }: { row: { original: ScheduledCommunicationList } }) => {
        const { isShown: editModalShown, toggle: editModalToggle } = useToggle()
        const { isShown: historyModalShown, toggle: historyModalToggle } = useToggle()

        return (
          <>
            {row.original.status === ScheduledCommunicationStatus.PendingApproval ||
            row.original.status === ScheduledCommunicationStatus.PendingDelivery ? (
              <>
                <button
                  className={'ml-2 mr-2 grid h-5 w-5 items-center justify-center'}
                  onClick={editModalToggle}
                  title="Edit Scheduled Communication"
                >
                  <EditIcon className="text-green-300" width={16} />
                </button>
                <BottomSheet isVisible={editModalShown} mobileFullHeight={true} onClose={editModalToggle}>
                  <EditScheduledCommunicationForm
                    refetchOnSuccess={refetchOnSuccess}
                    scheduledCommunication={row.original}
                    toggle={editModalToggle}
                  />
                </BottomSheet>
              </>
            ) : null}
            <button
              className="mr-2 grid h-5 w-5 items-center justify-center"
              onClick={historyModalToggle}
              title="View History"
            >
              <TimeFilledIcon className="w-5 cursor-pointer text-green-300" />
            </button>
            <div className="mr-2 grid h-5 w-5 items-center justify-center" title="View Reservation">
              <GuardedLink to={`/reservations/${row.original.reservationId}`}>
                <InfoFilledIcon className="w-5 cursor-pointer text-green-300" />
              </GuardedLink>
            </div>
            <div className="mr-2 grid h-5 w-5 items-center justify-center" title="View Member">
              <GuardedLink to={`/members/${row.original.reservation.member.id}`}>
                <OccupantsFilledIcon className="w-5 cursor-pointer text-green-300" />
              </GuardedLink>
            </div>
            {historyModalShown ? (
              <BottomSheet isVisible={historyModalShown} mobileFullHeight={true} onClose={historyModalToggle}>
                <History recordId={row.original.id} recordType={RecordType.SCHEDULED_COMMUNICATIONS} />
              </BottomSheet>
            ) : null}
          </>
        )
      },
    },
  ]
}
